import Hls, {
  AudioTrackLoadedData,
  AudioTracksUpdatedData,
  BufferCreatedData,
  FragChangedData,
  FragLoadedData,
  FragParsedData,
  LevelPTSUpdatedData,
  LevelSwitchedData,
  LevelUpdatedData,
  LevelsUpdatedData,
  ManifestParsedData,
  SubtitleTrackLoadedData,
  SubtitleTracksUpdatedData,
} from 'hls.js';
import { TimelineChart } from './timeline-chart';

/**
 * Wires hls.js playback events to a TimelineChart instance (ported from hls.js demo).
 * Returns a cleanup function that removes all listeners.
 */
export function addHlsTimelineListeners(hls: Hls, chart: TimelineChart): () => void {
  const onManifestLoading = () => {
    chart.reset();
  };

  const onManifestParsed = (_event: string, data: ManifestParsedData) => {
    chart.removeType('level');
    chart.removeType('audioTrack');
    chart.removeType('subtitleTrack');
    chart.updateLevels(data.levels);
  };

  const onBufferCreated = (_event: string, data: BufferCreatedData) => {
    if (hls.media) {
      chart.updateSourceBuffers(data.tracks, hls.media);
    }
  };

  const onBufferReset = () => {
    chart.removeSourceBuffers();
  };

  const onLevelsUpdated = (_event: string, data: LevelsUpdatedData) => {
    chart.removeType('level');
    chart.updateLevels(data.levels);
  };

  const onLevelSwitched = (_event: string, data: LevelSwitchedData) => {
    chart.removeType('level');
    chart.updateLevels(hls.levels, data.level);
  };

  const onLevelLoading = () => {
    chart.removeType('level');
    chart.updateLevels(hls.levels);
  };

  const onLevelUpdated = (_event: string, data: LevelUpdatedData) => {
    chart.updateLevelOrTrack(data.details);
  };

  const onLevelPtsUpdated = (_event: string, data: LevelPTSUpdatedData) => {
    chart.updateLevelOrTrack(data.details);
  };

  const onAudioTracksUpdated = (_event: string, data: AudioTracksUpdatedData) => {
    chart.removeType('audioTrack');
    chart.updateAudioTracks(data.audioTracks);
  };

  const onSubtitleTracksUpdated = (_event: string, data: SubtitleTracksUpdatedData) => {
    chart.removeType('subtitleTrack');
    chart.updateSubtitleTracks(data.subtitleTracks);
  };

  const onAudioTrackSwitched = () => {
    chart.removeType('audioTrack');
    chart.updateAudioTracks(hls.audioTracks);
  };

  const onSubtitleTrackSwitch = () => {
    chart.removeType('subtitleTrack');
    chart.updateSubtitleTracks(hls.subtitleTracks);
  };

  const onAudioTrackLoaded = (_event: string, data: AudioTrackLoadedData) => {
    chart.updateLevelOrTrack(data.details);
  };

  const onSubtitleTrackLoaded = (_event: string, data: SubtitleTrackLoadedData) => {
    chart.updateLevelOrTrack(data.details);
  };

  const updateFragment = (
    _event: string,
    data: FragLoadedData | FragParsedData | FragChangedData,
  ) => {
    if ('stats' in data && data.stats) {
      const legacyStats = data.stats as {
        retry?: number;
        loaded?: number;
        total?: number;
        trequest?: number;
        tfirst?: number;
        tload?: number;
      };
      const { retry, loaded, total, trequest, tfirst, tload } = legacyStats;
      if (trequest && tload) {
        Object.assign(data.frag.stats, {
          loaded: loaded ?? 0,
          retry: retry ?? 0,
          total: total ?? 0,
          loading: { start: trequest, first: tfirst ?? trequest, end: tload },
        });
      }
    }
    chart.updateFragment(data);
  };

  const onChartUpdate = () => {
    chart.update();
  };

  hls.on(Hls.Events.MANIFEST_LOADING, onManifestLoading);
  hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
  hls.on(Hls.Events.BUFFER_CREATED, onBufferCreated);
  hls.on(Hls.Events.BUFFER_RESET, onBufferReset);
  hls.on(Hls.Events.LEVELS_UPDATED, onLevelsUpdated);
  hls.on(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
  hls.on(Hls.Events.LEVEL_LOADING, onLevelLoading);
  hls.on(Hls.Events.LEVEL_UPDATED, onLevelUpdated);
  hls.on(Hls.Events.LEVEL_PTS_UPDATED, onLevelPtsUpdated);
  hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, onAudioTracksUpdated);
  hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, onSubtitleTracksUpdated);
  hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, onAudioTrackSwitched);
  hls.on(Hls.Events.SUBTITLE_TRACK_SWITCH, onSubtitleTrackSwitch);
  hls.on(Hls.Events.AUDIO_TRACK_LOADED, onAudioTrackLoaded);
  hls.on(Hls.Events.SUBTITLE_TRACK_LOADED, onSubtitleTrackLoaded);
  hls.on(Hls.Events.FRAG_LOADED, updateFragment);
  hls.on(Hls.Events.FRAG_PARSED, updateFragment);
  hls.on(Hls.Events.FRAG_CHANGED, updateFragment);
  hls.on(Hls.Events.BUFFER_APPENDING, onChartUpdate);
  hls.on(Hls.Events.BUFFER_APPENDED, onChartUpdate);
  hls.on(Hls.Events.BUFFER_FLUSHED, onChartUpdate);

  // Seed chart with current manifest state when attaching to an already-playing stream.
  if (hls.levels?.length) {
    chart.updateLevels(hls.levels, hls.currentLevel);
  }
  if (hls.audioTracks?.length) {
    chart.updateAudioTracks(hls.audioTracks);
  }

  return () => {
    hls.off(Hls.Events.MANIFEST_LOADING, onManifestLoading);
    hls.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
    hls.off(Hls.Events.BUFFER_CREATED, onBufferCreated);
    hls.off(Hls.Events.BUFFER_RESET, onBufferReset);
    hls.off(Hls.Events.LEVELS_UPDATED, onLevelsUpdated);
    hls.off(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
    hls.off(Hls.Events.LEVEL_LOADING, onLevelLoading);
    hls.off(Hls.Events.LEVEL_UPDATED, onLevelUpdated);
    hls.off(Hls.Events.LEVEL_PTS_UPDATED, onLevelPtsUpdated);
    hls.off(Hls.Events.AUDIO_TRACKS_UPDATED, onAudioTracksUpdated);
    hls.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, onSubtitleTracksUpdated);
    hls.off(Hls.Events.AUDIO_TRACK_SWITCHED, onAudioTrackSwitched);
    hls.off(Hls.Events.SUBTITLE_TRACK_SWITCH, onSubtitleTrackSwitch);
    hls.off(Hls.Events.AUDIO_TRACK_LOADED, onAudioTrackLoaded);
    hls.off(Hls.Events.SUBTITLE_TRACK_LOADED, onSubtitleTrackLoaded);
    hls.off(Hls.Events.FRAG_LOADED, updateFragment);
    hls.off(Hls.Events.FRAG_PARSED, updateFragment);
    hls.off(Hls.Events.FRAG_CHANGED, updateFragment);
    hls.off(Hls.Events.BUFFER_APPENDING, onChartUpdate);
    hls.off(Hls.Events.BUFFER_APPENDED, onChartUpdate);
    hls.off(Hls.Events.BUFFER_FLUSHED, onChartUpdate);
  };
}
